const gasConstant = {
  status: 'Active',
  name: 'Gas Constant',
  imageFileName: 'images/gas-constant-icon.jpg',
  imageAlt:
    'Three pieces of lab glassware rest on table. The flask on the left contains a a brown liquid and a test tube inside with the same brown solution. A rubber hose is attached to a short glass tube that is inserted in the 1 hole stopper that is attached at the top of the flask. The glassware in the middle is a Florence flask (round body fask with a flat bottrom). The flask about 200 milliliters of water. It has a two hole stopper where glass tubes are inserted. The hole on the left has a short tue, while the tube on the right is long nearly reaching the bottom of the flask. To the right is a 600 milliliter beaker containing about 300 milliliter of water. Inside the beaker is a rubber hose that has a short glass tube at its end that rests on the bottom of the beaker',
  description:
    'Experimental guide to determine the value of R from the ideal gas law.',
  tags: ['Pre-Lab', 'Wet Lab', 'Experiment', 'Chem-101A'],
  page: '/gas-constant',
  content: {
    overview: {
      name: 'Overview',
      link: 'overview',
      tabs: [
        {
          name: 'NOTAB',
          order: 1,
          steps: [
            {
              name: 'NOSTEP',
              order: 1,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Text',
                  text: 'In this experiment, you will determine the value of the gas constant, R, from the ideal gas law. You will measure the mass, volume, pressure, and temperature of a chemically generated sample of oxygen. Read through the <strong><em>Introduction</em></strong> to learn how to collect all data parameters of the ideal gas law equation. Next, review the <strong><em>Experiment</em></strong> and the complicated experimental set-up. Several tips are provided in videos and photos.',
                },
              ],
            },
          ],
        },
      ],
    },
    introduction: {
      name: 'Introduction',
      link: 'introduction',
      tabs: [
        {
          name: 'NOTAB',
          order: 1,
          steps: [
            {
              name: 'NOSTEP',
              order: 1,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Text',
                  text: 'The ideal gas law, PV = nRT, expresses the relationship between the pressure, volume, temperature, and quantity of an ideal gas. An ideal gas is a mathematical construct: the molecules have zero size and zero attraction for each other. Nonetheless, because of the relatively large distance between gas molecules under common pressure and temperature conditions, calculations for real gases work out quite well using the ideal gas law. The molar gas constant, R, has the same value for all gases under any conditions.',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'In this experiment, hydrogen peroxide is decomposed using the catalyst, iron(III) chloride. A catalyst causes the reaction to take place quickly. When a catalyst is present, it is placed above the arrow in the balanced chemical reaction equation since the catalyst is not consumed in the reaction.',
                },
                {
                  order: 3,
                  type: 'Image',
                  imageFileName: '/images/gas-constant/reaction.jpg',
                  imageAlt:
                    'A chemical reaction equation: H2O2(l) right arrow with above the arrow FeCl3 H2O(l) + 1/2 O2(g).',
                  centered: true,
                },
                {
                  order: 4,
                  type: 'Text',
                  text: 'Oxygen gas is produced and is used to ﬁnd R. You will measure the mass, volume, pressure, and temperature of a chemically generated sample of oxygen. This is how you will collect these data parameters:',
                },
                {
                  order: 3,
                  type: 'Header',
                  text: 'Mass',
                },
                {
                  order: 4,
                  type: 'Text',
                  text: 'The mass is measured by weighing the reaction ﬂask before and after the reaction takes place that liberates oxygen. The difference in mass is the mass of oxygen.',
                },
                {
                  order: 5,
                  type: 'Header',
                  text: 'Volume',
                },
                {
                  order: 6,
                  type: 'Text',
                  text: 'As the oxygen is generated, it will displace an equal volume of water from a connected ﬂask.',
                },
                {
                  order: 7,
                  type: 'Header',
                  text: 'Temperature',
                },
                {
                  order: 8,
                  type: 'Text',
                  text: 'The temperature will be the average temperature of the reaction vessel gas and the ﬂask that contained the water which was displaced.',
                },
                {
                  order: 9,
                  type: 'Header',
                  text: 'Pressure',
                },
                {
                  order: 10,
                  type: 'Text',
                  text: "Measuring the pressure requires the most care. One reason for this is that the oxygen is collected over water, so some of the pressure will be due to water vapor. Dalton's Law of Partial Pressures will be used to calculate the pressure of the oxygen. Another reason is that the apparatus has tight connections of rubber stoppers into the ﬂasks. When making such connections, pressure is exerted. A method of equalizing the pressure inside the apparatus with the pressure in the room will be used. In this way, the pressure shown on the barometer in the lab will be the pressure in the apparatus.",
                },
              ],
            },
          ],
        },
      ],
    },
    experiment: {
      name: 'Experiment',
      link: 'experiment',
      tabs: [
        {
          name: 'Start Here',
          order: 1,
          steps: [
            {
              name: 'Summary',
              order: 1,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Summary: Experimental Steps',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'The general experimental steps are: <li>Trick the balance balance to measure the mass of a liquid inside a flask</li><li>Assemble the reaction apparatus.</li><li>Mix the contents in the reaction flask and test tube to start the reaction.</li><li>Wait for the reaction to complete.</li><li>Get the average temperature by measuring the temperature of the reaction flask and the Florence flask.</li><li>Measure the volume of water collected.</li>',
                },
              ],
            },
            {
              name: 'Equipment',
              order: 2,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Equipment',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'The following pieces of equipment are needed for this experiment:',
                },
                {
                  order: 3,
                  type: 'Image',
                  imageFileName: '/images/gas-constant/equipment.jpg',
                  imageAlt:
                    'The equipment is shown spread out on the lab bench. Numbers are next to each and a key is to its right. 1. 125 mL flask, 2. one-hole stopper, 3. 50 mL graduated cylinder, 4. 500 mL Florence flask, 5. two-hole stopper, 6. 600 mL beaker, 7. thermometer, 8. 10x75 test tube, 9. disposable straw segment, 10. tweezers, 11. pimch lamp, 12. 3 short glass tubes, 13. 1 bent class tube, 14. counter-weight (stationed by the mass balance), 15. 2 rubber hoses (30 cm and 60 cm).',
                  centered: true,
                },
              ],
            },
            {
              name: 'Notebook Prep',
              order: 3,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Notebook Preparation',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'Since you will be calculating the value of R, re-write the ideal gas law equation in your notebook so R is isolated. Make sure your algebra is sound.',
                },
                {
                  order: 3,
                  type: 'Image',
                  imageFileName: '/images/gas-constant/R-units.jpg',
                  imageAlt:
                    'A rectangular space is provided. Inside is PV = nRT. Below it is R =.',
                  centered: true,
                },
                {
                  order: 4,
                  type: 'Text',
                  text: 'For the data collection table, Trial 2 is provided only if there is an error in trial 1 data. Draw the <strong>Data Table</strong> in your notebook:',
                },
                {
                  order: 5,
                  type: 'Image',
                  imageFileName: '/images/gas-constant/reaction-table.jpg',
                  imageAlt:
                    'A 8 row 3 column table. The first row lists the headers of the columns 1 to 3 as: Data Table, Trial 1 and Trial 2. Starting at row 2 to row 8: 1. Mass of the reaction flask assembly before reaction, 2. Mass of the reaction flask assembly after reaction, 3. Temperature of the gas in the reaction flask, 4. Temperature of the gas in the round bottomed flask, 5. Volume of O2 collected, 6. Barometer reading, and 7. Aqueous vapor pressure at average temperature in two flasks. The columns 2 and 3, units are listed. Row 2 and 3 have grams. Row 4 and 5 have degrees Celsius. Row 6 has inches of mercury. Row 7 has torr.',
                  centered: true,
                },
                {
                  order: 6,
                  type: 'Text',
                  text: 'Next, draw the <strong>Calculation Table</strong> in your notebook.',
                },
                {
                  order: 7,
                  type: 'Image',
                  imageFileName: '/images/gas-constant/calculation-table.jpg',
                  imageAlt:
                    'A 8 row 4 column table. The first row lists the headers of the columns 1 to 3 as: Calculation Table, Calculation Space Trial 1 and Trial 2. Starting at row 2 to row 8: Mass of O2, n = Moles O2, T = Average Temperature, V= Volume of O2 collected, P = Pressure of O2 alone in flask, Rexperimental = Value of R from the data, and % error = R sub exp minus R sub book divided by R sub book times 100. Under the % error is Book value of R = 62.4 L times Torr per K per mol. The columns 2 row 2 to 8 are empty ready for calculations to take place. Columns 3 and 4 row have the same units. For row 2, the units are grams. Row 3 has moles. Row 4 has Kelvin. Row 5 has liters. Row 6 has L times Torr per K per mol. Row 6 has torr. Row 7 has L times Torr per K per mol. Row 8 has %.',
                  centered: true,
                },
              ],
            },
          ],
        },
        {
          name: 'Procedure',
          order: 2,
          steps: [
            {
              name: 'Chemical Prep',
              order: 1,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Chemical Preparation',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'Pour the 3% H<sub>2</sub>O<sub>2</sub> into the 125 ml ﬂask.',
                },
                {
                  order: 3,
                  type: 'Text',
                  text: 'Moisten one of the straight short glass tubes and insert it into the one-holed rubber stopper. ',
                },
                {
                  order: 4,
                  type: 'Text',
                  text: 'Half fill the 10 x 75 test tube with 3 M FeCl<sub>3</sub>. (The transfer pipette in the fume hood next to the FeCl<sub>3</sub> <em>may</em> have a mark to designate the volume or you.) Use the forceps to lower the filled test tube into the ﬂask. Put the stopper ﬁrmly into the ﬂask.',
                },
                {
                  order: 5,
                  type: 'Text',
                  text: 'Watch: Adding the test tube into the flask <p><iframe width="320" height="541" src="https://www.youtube.com/embed/BStVZoScB1M?&playlist=BStVZoScB1M&loop=1" title="Preparing for the Decomposition of Hydrogen Peroxide" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></p>',
                },
              ],
            },
            {
              name: 'Balance Trick',
              order: 2,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Tricking the Mass Balance',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'Weigh the reaction ﬂask assembly to the nearest 0.001 g and record the mass in the data table. To do so, you have to “trick” the balance to read to the nearest 0.001 g, because the weight of the reaction ﬂask assembly causes the balance to shift to a less sensitive mode. This <em>trick</em> only works if you are comparing two masses to one another, before and after some event, since you will not know the absolute mass of the ﬂask assembly. ',
                },
                {
                  order: 3,
                  type: 'Text',
                  text: 'Next to each balance is a counterweight. Each counterweight is speciﬁc for each balance. ',
                },
                {
                  order: 4,
                  type: 'Text',
                  text: 'Before weighing the ﬂask, put the counterweight on the pan, put on the draft shield, zero  the weight out, and remove the counterweight. Now put the ﬂask on the pan, then the draft shield, and read the displayed mass. ',
                },
                {
                  order: 5,
                  type: 'Images',
                  images: [
                    {
                      imageFileName: '/images/gas-constant/countered.jpg',
                      imageAlt:
                        'The milligram mass balance has on its balance a flask with an empty test tube inside. A large plastic beaker covers the balance to prevent air currents from impacting the measurement. The display reads 2.325 g.',
                    },
                    {
                      imageFileName: '/images/gas-constant/no-counter.jpg',
                      imageAlt:
                        'The milligram mass balance has on its balance a flask with an empty test tube inside. A large plastic beaker covers the balance to prevent air currents from impacting the measurement. The display reads 92.48 g',
                    },
                  ],
                },
                {
                  order: 6,
                  type: 'Text',
                  text: 'How can you tell which picture had used the counter-weight? (select all that apply)',
                },
                {
                  order: 7,
                  type: 'Multiple Choice',
                  choices: [
                    {
                      label: 'The one with the lower mass has been countered.',
                      tip: 'The mass that appears will not be the actual mass of the glassware because part of the mass has been subtracted by the counter-weight. So a lower expected mass for such glassware is expected.',
                      validChecked: true,
                    },
                    {
                      label: 'The one with the higher mass has been countered.',
                      tip: 'The higher mass is the actual mass of the glassware. That has not been countered. Countered means the actual mass has been subtracted by the counter-weight, so it should be expected to be a lower mass.',
                      validChecked: false,
                    },
                    {
                      label:
                        "The one that reached the milligram digit's place.",
                      tip: 'The counter <em>tricks</em> the balance to measure with more precision. ',
                      validChecked: true,
                    },
                    {
                      label: "The one that reached the decigram digit's place.",
                      tip: "If the mass was not countered, the mass will be too heavy for the balance, so it will lose its precision. The mass will display to the hundredth's place.",
                      validChecked: false,
                    },
                  ],
                },
                {
                  order: 6,
                  type: 'Text',
                  text: 'Use the same procedure when you re-weigh the ﬂask after the reaction.',
                },
              ],
            },
            {
              name: 'Apparatus Asembly',
              order: 3,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Apparatus Assembly',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'Moisten the glass to the rubber hoses and follow the assembly as shown in then video and diagram below.',
                },
                {
                  order: 3,
                  type: 'Text',
                  text: 'Watch: How to Assemble the Glassware <p><iframe width="560" height="315" src="https://www.youtube.com/embed/6aosnBCZ0-o?&playlist=6aosnBCZ0-o&loop=1" title="How to Assemble the Glassware" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></p>',
                },
                {
                  order: 4,
                  type: 'Text',
                  text: 'After watching the video, review the summary of the set-up.',
                },
                {
                  order: 5,
                  type: 'Image',
                  imageFileName:
                    '/images/gas-constant/assembly-instructions.jpg',
                  imageAlt:
                    'A diagram provides two parts. The top section shows the following: On the left is a drawing of a 125 mL flask with a stopper and a short tube through it. The flask is labeled as Reaction Flask. In the middle is a round flask with a flat bottom A two-hole stopper is lodged at its top. A hose is attached to a short tube and the tube is lodged into one of the holes of the stopper. A long L-shaped tube is in the other hole of the stopper. The L-shape tube nearly reaches the bottom of the round flask. On the other end of the L-shaped tube is a hose that is connected to a hose that spirals into an open 600 mL beaker. At the end of the hose is a short tube resting at the bottom of the beaker. A clamp is pointed to the hose to indicate it will be pinched off. There is a small amount of liquid in the reaction flask, the tube is half full with liquid, the round flask has liquid to the top of its nect and the beaker has liquid a little under half full. Directions are written with steps 1 and 2 on the left side of  this set-up. Step 1 reads "Put tap water into the collection flassware as shown". Step 2 reads: "Put in the straw segment, then blow, until all air is out of the hose leading to the beaker". An arrow points points to the hose attached to the short tube of the round flask. Below the flask and beaker is the label, Collection Glassware. To the right of the diagram is step 3. Step 4 reads: "Raise the beaker to allow water to flow into the round flask until it reaches the level shown. Now place the clamp on the rubber hose". An arrow points to the pinch clamp. The bottom section shows the same set-up as above except that the 600 mL beaker on the right is raised  so that the liquid level in the beaker match the level as the liquid in the neck of the round flask. A double arrow also points to each of these liquid levels. The pinch clamp is held by a thumb and index finger and has pinched the hose that connects the round flask and beaker. Directions are written around the set-up. Step 4 reads "Attach the hose to the previously weighed reaction flask". An arrow points to the one-hole stopper.  Above the beaker is step 5. Step 5 reads, "While holding the beaker so its water level is at the same height as the water level in the flask, squeeze the clamp open, then allow it to clamp shut. This equalizes the pressure. [In bold] Dump the water of the beaker". Under the beaker is step 6. Step 6 reads: "Set the beaker down and [in bold] remove the clamp. A few ml of water will run out. If water continues to flow, there is a leak, and connctions should be checked.',
                  centered: true,
                },
              ],
            },
            {
              name: 'Reaction',
              order: 4,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Reaction',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'Watch the video to observe the careful maneuvering when mixing the catalyst and hydrogen peroxide together.',
                },
                {
                  order: 3,
                  type: 'Text',
                  text: 'Watch: Carrying out the reaction <p><iframe width="560" height="315" src="https://www.youtube.com/embed/XdkozrReXJU?&playlist=XdkozrReXJU&loop=1" title="Decomposition" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></p>',
                },
                {
                  order: 4,
                  type: 'Text',
                  text: 'After watching the video, review the summary of the reaction set-up.',
                },
                {
                  order: 5,
                  type: 'Image',
                  imageFileName: '/images/gas-constant/reaction-assembly.jpg',
                  imageAlt:
                    'A diagram is of the reaction flask and collection glassware. On the left is a 125 mL flask with a stopper and a short tube through it. The flask is labeled as Reaction Flask. In the middle is a round flask with a flat bottom. A two-hole stopper is lodged in its top end. A hose is attached to a short tube and the tube is lodged into one of the holes of the stopper. A long L-shaped tube is in the other hole of the stopper. The L-shape tube nearly reaches the bottom of the round flask. On the other end of the L-shaped tube is a hose that is connected to a hose that spirals into an open 600 mL beaker. At the end of the hose is a short tube resting at the bottom of the beaker. A clamp is pointed to the hose to indicate it will be pinched off. There is a small amount of liquid in the reaction flask, the tube is half full with liquid, the round flask has liquid to the top of its nect and the beaker has liquid a little under half full. Directions are written around the diagram. Step 7: "Mix the reagants." Step 8 is above the beaker and reads "When the reaction is over and the reaction flask has cooled to room temperature (estimate by feel), raise the collection flask so its water level is at the same height as the water level in the beaker. [In bold] Hold it there while you attach the clamp onto the rubber hose. Remove the hose from the collection beaker, and disconnect the hose from the reaction flask. Step 9 is to the right of the beaker and reads: "Reweigh the reacdtion flask, measure the tempearutre of the oxygen in each flask, then measure the volume of water in the beaker."',
                  centered: true,
                },
              ],
            },
            {
              name: 'Data Collection',
              order: 5,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Data Collection',
                },
                {
                  order: 2,
                  type: 'Header',
                  text: 'Mass',
                },
                {
                  order: 3,
                  type: 'Text',
                  text: 'Re-weigh the reaction flask (using the same mass balance and counter-weight). The difference in the mass of the reaction ﬂask before and after the reaction is due to the oxygen given off during the reaction.',
                },
                {
                  order: 4,
                  type: 'Header',
                  text: 'Volume',
                },
                {
                  order: 5,
                  type: 'Text',
                  text: 'Use the large graduated cylinders on the lab cart to measure the volume of water in your beaker and record it in the data table. This volume is the same as the volume of oxygen generated in the reaction ﬂask (if all went well). ',
                },
                {
                  order: 6,
                  type: 'Images',
                  images: [
                    {
                      imageFileName:
                        '/images/gas-constant/water-collection.jpg',
                      imageAlt:
                        'A 1 liter graduated cylinder and a 600 mL beaker filled about half way with water.',
                    },
                    {
                      imageFileName:
                        '/images/gas-constant/volume-measurements.jpg',
                      imageAlt:
                        'A close-up view of a 1 L graduated cylinder . The water reacher between 370-380 milliliteres.',
                    },
                  ],
                },
                {
                  order: 7,
                  type: 'Input',
                  directions:
                    'For practice, measure the volume of water in mL from our experiment?',
                  validAnswers: ['377', '378', '379'],
                  unitOfMeasure: 'mL',
                  tip: 'If you notice the bottom of the menuscus is just under the graduated line. A reading should be amde just below it with the estimated digit at the ones place.',
                },
                {
                  order: 8,
                  type: 'Header',
                  text: 'Temperature',
                },
                {
                  order: 9,
                  type: 'Text',
                  text: 'Measure the temperature of the O<sub>2</sub> in each flask (the Florence flask and the reaction flask). Insert your thermometer into the gas in each ﬂask, wait 30 seconds, then read and record the temperature in the data table.',
                },
                {
                  order: 10,
                  type: 'Images',
                  images: [
                    {
                      imageFileName: '/images/gas-constant/temp-check-A.jpg',
                      imageAlt:
                        'A 125 mL flask has a yellow solution and a test tube filled to its brim. A thermometer is also inside and rests at the bottom.',
                      imageTitle: 'A',
                    },
                    {
                      imageFileName: '/images/gas-constant/temp-check-B.jpg',
                      imageAlt:
                        'A 125 mL flask has a yellow solution and a test tube filled to its brim. A thermometer is also inside, but held by hand and above the liquid.',
                      imageTitle: 'B',
                    },
                  ],
                },
                {
                  order: 11,
                  type: 'Input',
                  directions:
                    'Which is the correct way to measure the temperature in the flasks?',
                  validAnswers: ['B', 'b'],
                  tip: 'The procedure says to "Insert your thermometer <strong>into the gas</strong> in each ﬂask". This means, the thermometer is not to touch the liquid. Hold the thermometer above any liquid and do not touch the side walls.',
                },
                {
                  order: 12,
                  type: 'Header',
                  text: 'Pressure',
                },
                {
                  order: 13,
                  type: 'Text',
                  text: "Measure the pressure of the room given by the barometer that you will find in a specified location in the lab (most likely, the instructor's lab bench).",
                },
                {
                  order: 14,
                  type: 'Image',
                  imageFileName: '/images/gas-constant/barometer.jpg',
                  imageAlt:
                    'A digital barometer resting on the lab bench showing the air pressure of 29.71 inHg at the bottom of its display screen',
                  centered: true,
                },
                {
                  order: 15,
                  type: 'Text',
                  text: 'However, the reaction ﬂask and the Florence ﬂask are both ﬁlled with oxygen saturated with water vapor. We must subtract the vapor pressure from the atmospheric pressure, to obtain the pressure of the produced oxygen. To ﬁnd out how much water vapor there is, you must use the average temperature collected from the reaction ﬂask and the Florence ﬂask. If your average is in between two temperatures, then take the average of their corresponding vapor pressures.',
                },
                {
                  order: 16,
                  type: 'Image',
                  imageFileName:
                    '/images/gas-constant/vapor-pressure-table.jpg',
                  imageAlt:
                    'A vapor pressure table. The left column is labeled temperature (degrees Celsius). Below are the temperatures down a column: 15 to 30. The right column is laeled VP (torr). Below are the vapor pressure values corresponding to the temperatures. The pairs are: 15 degrees Celsius and 12.8 torr, 16 and 13.6, 17 and 14.5, 17 and 15.5, 19 and 16.5, 20 and 17.5, 21 and 18.6, 22 and 19.8, 23 and 21.1, 24 and 22.4, 25 and 23.8, 26 and 25.2, 27 and 26.7, 28 and 28.3, 29 and 30.0 and last 30 and 31.8.',
                  centered: true,
                },
                {
                  order: 17,
                  type: 'Header',
                  text: 'Clean-up',
                },
                {
                  order: 18,
                  type: 'Text',
                  text: 'Clean-up: Before returning the kit, disconnect all pieces, clean and dry them, and discard the straw.',
                },
                {
                  order: 19,
                  type: 'Text',
                  text: 'You are ﬁnished with the experiment guide!',
                  centered: true,
                },
              ],
            },
          ],
        },
      ],
    },
  },
  rank: 15,
};

export default gasConstant;
